import * as Columns from './Columns'

/**
 * Creates a column component based on the columnType specified in the column definition.
 *
 * Available column types:
 * - 'text': Standard text column with optional filtering/sorting
 * - 'numeric': Numeric column with equals filter
 * - 'date': Date column with date picker filter
 * - 'time': Time column with time picker editor
 * - 'year': Year column with numeric filter
 * - 'idaCode': IDA code column with special formatting
 * - 'channel': Channel dropdown column
 * - 'contributiontype': Contribution Type dropdown column
 * - 'contributorname': Contributor Name dropdown column
 * - 'boolean': Boolean column with checkbox display
 * - 'membershiptypes': MembershipTypes column displaying array/comma-separated values as badges
 * - 'actions': Action buttons column
 *
 * @param {Object} params - Column configuration
 * @param {string} params.columnType - Type of column to create (required)
 * @param {string} params.title - Column header title
 * @param {string} params.mapsToField - Field name in data
 * @param {boolean} params.filter - Enable filtering
 * @param {boolean} params.sortable - Enable sorting
 * @param {boolean} params.editable - Enable editing
 * @param {Function} params.template - Custom display template
 * @param {Function} params.isFieldChanged - Function to check if field changed
 * @param {Function} params.isTimeFieldChanged - Function to check if time field changed
 * @param {Object} params.validationErrors - Validation errors object
 * @param {string} params.dataKey - Primary key field name
 * @param {Object} params.selectedChannels - Selected channels state (for channel type)
 * @param {Function} params.setSelectedChannels - Set selected channels (for channel type)
 * @param {Array} params.channels - Available channels (for channel type)
 * @param {Array} params.contributionTypes - Available contribution types (for contributiontype type)
 * @param {Object} params.contributorNames - Available contributor names by author key (for contributorname type)
 * @returns {JSX.Element} Column component
 */
export const createColumn = ({
    columnType,
    title,
    mapsToField,
    filter,
    expand,
    template,
    sortable,
    editable,
    isFieldChanged,
    isDateFieldChanged,
    isTimeFieldChanged,
    isFieldChangedOverride,
    validationErrors,
    dataKey,
    editingRowKey,
    selectedChannels,
    setSelectedChannels,
    channels,
    contributionTypes,
    contributorNames,
    originalData,
    min,
    max,
    episodeDuration,
    updateRowField,
    updateRowFields,
    editorCallbacksRef,
    toastRef,
    contactTypeIds,
    contributionTypeIds,
    filterElement,
    filterMatchMode,
    className
}) => {
    const columnProps = {
        title,
        mapsToField,
        filter,
        sortable,
        editable,
        isFieldChanged,
        isDateFieldChanged,
        isTimeFieldChanged,
        isFieldChangedOverride,
        validationErrors,
        template,
        dataKey,
        editingRowKey,
        originalData,
        min,
        max,
        episodeDuration,
        updateRowField,
        filterElement,
        filterMatchMode,
        className
    }

    // Column type configuration with components and extra props getters
    const columnConfig = {
        'text': { component: Columns.TextColumn },
        'numeric': { component: Columns.NumericColumn },
        'date': { component: Columns.DateColumn },
        'time': { component: Columns.TimeColumn },
        'year': { component: Columns.YearColumn },
        'idaCode': { component: Columns.IdaCodeColumn },
        'boolean': { component: Columns.BooleanColumn },
        'membershiptypes': { component: Columns.MembershipTypesColumn },
        'worktype': { component: Columns.WorkTypeColumn },
        'workcontributiontype': { component: Columns.WorkContributionTypeColumn },
        'avtype': { component: Columns.AvTypeColumn },
        'channel': {
            component: Columns.ChannelColumn,
            extraProps: { dataKey, selectedChannels, setSelectedChannels, channels }
        },
        'contributiontype': {
            component: Columns.ContributionTypeColumn,
            extraProps: { contributionTypes, editorCallbacksRef, dataKey, contactTypeIds, contributionTypeIds }
        },
        'contributorname': {
            component: Columns.ContributorNameColumn,
            extraProps: { contributorNames }
        },
        'contributionpercentage': {
            component: Columns.ContributionPercentageColumn,
            extraProps: { episodeDuration, updateRowField, editorCallbacksRef, toastRef, dataKey }
        },
        'contributionminutes': {
            component: Columns.ContributionMinutesColumn,
            extraProps: { episodeDuration, updateRowField, editorCallbacksRef, toastRef, dataKey }
        },
        'expander': {
            component: Columns.ExpanderColumn,
            propsOverride: { title, expand }
        },
        'actions': {
            component: Columns.ActionsColumn,
            propsOverride: { template, dataKey, editingRowKey }
        }
    }

    // Get column configuration or default to text
    const config = columnConfig[columnType] || columnConfig['text']
    const { component: ColumnComponent, extraProps = {}, propsOverride } = config

    // Use propsOverride if provided (for actions column), otherwise merge columnProps with extraProps
    const finalProps = propsOverride || { ...columnProps, ...extraProps }

    return ColumnComponent(finalProps)
}
